import { useCallback, useState } from 'react';
import { useDispatch, useStore } from 'react-redux';
import { changeClassRoom } from '@core/actions/classRoom';
import { accessToken as getAccessToken } from '@core/selectors/user';
import { fetchAssignment } from '@core/assignments/api/fetch';
import { HomeUpcomingAssignment } from '@core/assignments/api/homeUpcomingAssignments';
import showToast from '@core/toasts/operations/showToast.operation';
import push from '@app/utils/routing';
import launchHandin from '@app/components/presentational/ClassRoom/Assignments/utils/launchHandin';
import appendFromHomeQueryParam from './appendFromHomeQueryParam';
import {
  isHandinProvider,
  isLti1p3Provider,
} from './normalizeAssignmentProvider';

type LtiLaunchState = {
  providerId: string;
  resourceLinkId: string;
  title: string;
  assignmentId: string;
} | null;

const getFallbackPath = (assignment: HomeUpcomingAssignment) => {
  if (assignment.cta.path) {
    return assignment.cta.path;
  }

  return `/dashboard/${assignment.spaceId}/journey/assignments?openAssignmentId=${assignment.id}`;
};

const useOpenUpcomingAssignment = () => {
  const dispatch = useDispatch();
  const store = useStore();
  const [ltiLaunch, setLtiLaunch] = useState<LtiLaunchState>(null);
  const [isOpening, setIsOpening] = useState(false);

  const clearLtiLaunch = useCallback(() => {
    setLtiLaunch(null);
  }, []);

  const handleLtiLaunchError = useCallback(() => {
    setLtiLaunch(null);
    dispatch(
      showToast({
        message: 'There was an error when trying to open the LTI tool.',
      }) as any
    );
  }, [dispatch]);

  const resolveHandinExternalId = useCallback(
    async (assignment: HomeUpcomingAssignment) => {
      if (assignment.externalId) {
        return assignment.externalId;
      }

      const state = store.getState();
      const accessToken = getAccessToken(state, assignment.spaceId);

      if (!accessToken) {
        return null;
      }

      try {
        const result = await fetchAssignment({
          accessToken,
          assignmentId: assignment.id,
        });
        return result?.data?.assignment?.externalId || null;
      } catch {
        return null;
      }
    },
    [store]
  );

  const openAssignment = useCallback(
    async (assignment: HomeUpcomingAssignment) => {
      if (assignment.cta.disabled || isOpening) {
        return;
      }

      setIsOpening(true);

      try {
        await dispatch(changeClassRoom(assignment.spaceId) as any);

        if (assignment.role === 'educator') {
          const submissionsPath =
            assignment.cta.path ||
            `/dashboard/${assignment.spaceId}/journey/assignments/${assignment.id}/submissions`;
          push(appendFromHomeQueryParam(submissionsPath));
          return;
        }

        if (isHandinProvider(assignment.provider)) {
          const externalId = await resolveHandinExternalId(assignment);

          if (externalId) {
            launchHandin({
              assignmentId: externalId,
              spaceId: assignment.spaceId,
            } as any);
            return;
          }
        }

        if (
          isLti1p3Provider(assignment.provider) &&
          assignment.providerId &&
          assignment.resourceLinkId
        ) {
          // Defer so Lti1p3Launch mounts after space switch settles.
          window.requestAnimationFrame(() => {
            setLtiLaunch({
              providerId: assignment.providerId as string,
              resourceLinkId: assignment.resourceLinkId as string,
              title: assignment.title,
              assignmentId: assignment.id,
            });
          });
          return;
        }

        // Turnitin / Quizzes / unknown — use Assignments page launch flow.
        push(appendFromHomeQueryParam(getFallbackPath(assignment)));
      } finally {
        setIsOpening(false);
      }
    },
    [dispatch, isOpening, resolveHandinExternalId]
  );

  return {
    openAssignment,
    ltiLaunch,
    clearLtiLaunch,
    handleLtiLaunchError,
  };
};

export default useOpenUpcomingAssignment;
